import { Modal, ModalWidth } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import { useGlobalModalContext } from '../../../GlobalModal';
import styles from './CoreScoreViewModal.module.scss';
import CoreIcon from '../../CoreScore/Static/CoreIcon';
import StrategicIcon from '../../CoreScore/Static/StrategicIcon';
import ModerateRiskIcon from '../../CoreScore/Static/ModerateRiskIcon';
import SpeculativeIcon from '../../CoreScore/Static/SpeculativeIcon';

const cx = classNames.bind(styles);

const CoreScoreViewModal = (): React.ReactElement => {
  const { closeModal } = useGlobalModalContext();

  return (
    <Modal
      width={ModalWidth.Large}
      open
      onClose={closeModal}
      headerTitle="CoreScore"
      dataTestId="core-score-view-modal"
    >
      <div className={cx('body')}>
        <p>
          The CoreScore is our analysts&apos; assessment of credit quality, which the analysts use to inform and
          underwrite their relative value recommendations. The CoreScore is framed by six underlying credit factors and
          is represented on a four-point scale (Core, Strategic, Moderate Risk, and Speculative) spanning the range from
          very stable, high-quality credit to very weak, but still expected to deliver par recovery. The CoreScore is
          meant to inform an investor&apos;s buy-and-hold investment decision and to identify opportunities for total
          return clients on a long-term investment horizon. Below, we provide definitions for our four-point scale:
        </p>
        <div className={cx('background-green', 'scores')}>
          <CoreIcon />
          <p className={cx('heading')}>
            <strong>Core</strong>
          </p>
        </div>
        <p>
          These are core, low-risk investments. Corporate financial policies are likely to be conservative, well
          communicated and consistently followed through the cycle.
        </p>
        <div className={cx('background-green', 'scores')}>
          <StrategicIcon />
          <p className={cx('heading')}>
            <strong>Strategic</strong>
          </p>
        </div>
        <p>
          These have additional risks relative to Core holdings. Corporate financial policies are typically well-
          structured to weather business and economic cycles, but management strategies could make modest credit
          deterioration a consideration.
        </p>
        <div className={cx('background-yellow', 'scores')}>
          <ModerateRiskIcon />
          <p className={cx('heading')}>
            <strong>Moderate Risk</strong>
          </p>
        </div>
        <p>
          Corporate financial policies are less likely to be well-insulated through the cycle where M&A or a deep cycle
          trough would likely contribute to credit deterioration.
        </p>
        <div className={cx('background-red', 'scores')}>
          <SpeculativeIcon />
          <p className={cx('heading')}>
            <strong>Speculative</strong>
          </p>
        </div>
        <p>
          These are the highest risk investments in our framework, but where we still see a probability of par
          repayment. Corporate financial policies are more aggressive, likely prioritizing equity returns over
          through-the-cycle insulation from credit risks. Cash flows are more likely to be volatile, limiting
          flexibility to adapt to negative catalysts.
        </p>

        <hr className={cx('divider-space')} />
        <p>
          Our analysts have published an assessment (i.e. current view) and an outlook (i.e. a greater than 1 year view)
          for each of the six factors. There are two schemes to CoreScore: Corporates and Financials. Our analysts rank
          all of the credits in each of the six factors on a relative basis within the analyst’s coverage area. Those
          scores are then calibrated across CreditSights coverage using market and quantitative indications. The six
          factors are equal weighted.
        </p>
        <p className={cx('text-space')}>
          The six factors in the{' '}
          <span className={cx('heading')}>
            <strong>Corporate</strong>
          </span>{' '}
          scheme are:
        </p>
        <p className={cx('text-space', 'left-space')}>
          <span className={cx('heading')}>
            <strong> 1. Leverage - </strong>
          </span>
          Financial risk, measured by the amount of debt on the balance sheet, relative to earnings/EBITDA/cash
          flow/assets.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>2. Cash flow - </strong>
          </span>
          An indication of a company&apos;s financial flexibility derived from the cash generated from operations
          against capital deployed for investments.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>3. Liquidity - </strong>
          </span>
          The company&apos;s ability to source short-term capital to meet its funding needs, from internal and/or
          external sources.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>4. Profitability - </strong>
          </span>
          The strength of a company&apos;s earnings, from the relevant perspectives of earnings quality, margins, and
          volatility.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>5. Event Risk - </strong>
          </span>
          A measurement of anticipated negative impact from some type of event, such as M&A, regulatory action,
          competition, litigation, etc. The measurement incorporates the probability and severity of the potential
          event.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            {' '}
            <strong>6. Sector Risk - </strong>
          </span>
          A measurement of risks latent in the sector, i.e. that are not specific to the credit. These would include
          industry cycle risks, sensitivity to the economic cycle, PE activity, etc.
        </p>

        <hr className={cx('divider-space')} />
        <p className={cx('text-space')}>
          The six factors in the{' '}
          <span className={cx('heading')}>
            <strong>Financials</strong>
          </span>{' '}
          scheme are:
        </p>
        <p className={cx('text-space', 'left-space')}>
          <span className={cx('heading')}>
            <strong>1. Capital Position / Leverage - </strong>
          </span>
          The size and quality of regulatory capital relative to risk-weighted assets and leverage exposure, and
          loss-absorbing capacity.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>2. Asset Quality - </strong>
          </span>
          The scale of, and trends in, credit quality of the loan book, investment portfolio, and other financial
          assets, including non-performing exposures and credit loss reserves.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>3. Liquidity - </strong>
          </span>
          The extent to which a bank or insurer holds sufficient cash (or assets that can easily be converted into cash)
          to repay depositors, creditors, and/or policyholders.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>4. Profitability - </strong>
          </span>
          The strength of a company&apos;s earnings, from the relevant perspectives of earnings quality, margins, cost
          ratios, volatility and diversification.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>5. Event Risk - </strong>
          </span>
          A measure of anticipated negative impact from some type of event, such as M&A, regulatory action, competition,
          etc. This incorporates business model risk. The measurement incorporates the probability and severity of the
          potential event.
        </p>
        <p className={cx('left-space')}>
          <span className={cx('heading')}>
            <strong>6. Sector Risk - </strong>
          </span>
          This captures risks latent in the sector and jurisdiction, that are not specific to the credit. These include
          sensitivity to the economic cycle, regulatory developments, etc.
        </p>
      </div>
    </Modal>
  );
};

export default CoreScoreViewModal;
